import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api.ts";
import { useStaff } from "../lib/staff.ts";

function shortFp(fp: string) {
  return fp.length > 20 ? `${fp.slice(0, 8)}…${fp.slice(-8)}` : fp;
}

export function StaffAttestCasPage() {
  const qc = useQueryClient();
  // Block / unblock are admin-only on the server; hide the buttons otherwise.
  const { role } = useStaff();
  const canMutate = role === "admin";

  const list = useQuery({
    queryKey: ["staff", "attest-cas"],
    queryFn: api.staffListAttestCas,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["staff", "attest-cas"] });
  const block = useMutation({
    mutationFn: (fp: string) => api.staffBlockAttestCa(fp),
    onSuccess: invalidate,
  });
  const unblock = useMutation({
    mutationFn: (fp: string) => api.staffUnblockAttestCa(fp),
    onSuccess: invalidate,
  });
  const busy = block.isPending || unblock.isPending;
  const err = (block.error ?? unblock.error) as Error | null;

  return (
    <>
      <h1>Attestation CAs</h1>
      <p className="muted">
        Infineon OPTIGA ECC intermediate CAs the device-enrollment path trusts.
        {" "}<b>Pinned</b> are compiled into the API (CA071 / CA066); <b>auto</b> are
        learned at enroll — an EK from a new TPM production batch, fetched from its
        AIA and verified to chain to the pinned Root CA 2. <b>Blocking</b> a CA
        rejects every EK under it at enrollment, even though it chains to the root
        — use it to shut out an unrecognised batch.
      </p>

      {err && <p className="err">{err.message}</p>}
      {list.isLoading && <p className="muted">Loading…</p>}
      {list.error && <p className="err">Couldn't load — {(list.error as Error).message}</p>}
      {list.data && (
        <table>
          <thead>
            <tr>
              <th>CA</th>
              <th>Source</th>
              <th>Fingerprint (SHA-256)</th>
              <th>First seen</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {list.data.cas.length === 0 && (
              <tr><td colSpan={6} className="muted">No CAs.</td></tr>
            )}
            {list.data.cas.map((ca) => (
              <tr key={ca.fingerprint} style={ca.blocked ? { opacity: 0.55 } : undefined}>
                <td>{ca.issuerCn}</td>
                <td>{ca.source}</td>
                <td><code title={ca.fingerprint}>{shortFp(ca.fingerprint)}</code></td>
                <td>
                  {ca.source === "auto" && ca.firstSeenAt ? (
                    <span title={ca.firstSeenDevice ?? undefined}>
                      {new Date(ca.firstSeenAt).toLocaleString()}
                    </span>
                  ) : (
                    <span className="muted">—</span>
                  )}
                </td>
                <td>
                  {ca.blocked ? (
                    <span style={{ color: "#dc2626", fontWeight: 600 }}>Blocked</span>
                  ) : (
                    <span style={{ color: "#16a34a" }}>Trusted</span>
                  )}
                </td>
                <td>
                  {canMutate && (ca.blocked ? (
                    <button
                      className="secondary"
                      disabled={busy}
                      onClick={() => unblock.mutate(ca.fingerprint)}
                    >
                      Unblock
                    </button>
                  ) : (
                    <button
                      className="secondary"
                      disabled={busy}
                      onClick={() => {
                        if (confirm(`Block ${ca.issuerCn}?\n\nEvery EK issued by this CA will be REJECTED at enrollment.`))
                          block.mutate(ca.fingerprint);
                      }}
                    >
                      Block
                    </button>
                  ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}
